import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { LoadingBlock } from "@/modules/boxDashboard/components/loadingBlock"
import {
  DailyScheduleCard,
  SLOT_GAP,
  SLOT_HEIGHT,
} from "@/modules/boxDashboard/modules/DailySchedule/components/dailyScheduleCard"
import { useDailySchedule } from "@/modules/boxDashboard/modules/DailySchedule/hooks/useDailySchedule"
import { createDailyScheduleModel } from "@/modules/boxDashboard/modules/DailySchedule/models/dailyScheduleModel"
import { useBoxSlotSidebar } from "@/modules/boxSlot/hooks/useBoxSlotSidebar"
import ProgrammationEmpty from "@images/empty/programmation_empty.svg"
import { word } from "@modules/core/utils/i18n"
import { useBoxNavigate } from "@modules/navigation/hooks/useBoxNavigate"
import { Path } from "@modules/navigation/routes"
import { EmptyView } from "@modules/ui/components/emptyView"
import { Suspense, useEffect, useMemo, useRef, useState } from "react"

export function DailyScheduleBlock() {
  return (
    <Suspense fallback={<LoadingBlock className="h-141" />}>
      <DailyScheduleBlockContent />
    </Suspense>
  )
}

function DailyScheduleBlockContent() {
  const [showAll, setShowAll] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const boxNavigate = useBoxNavigate()
  const { navigateToBoxSlotView } = useBoxSlotSidebar()

  const { slots } = useDailySchedule()

  const model = useMemo(() => createDailyScheduleModel(slots), [slots])

  const slotsToDisplay = showAll ? model.allSlots : model.visibleSlots

  // When expanding, scroll to show the same view as collapsed
  useEffect(() => {
    if (showAll && scrollContainerRef.current && model.visibleStartIndex > 0) {
      const scrollPosition = model.visibleStartIndex * (SLOT_HEIGHT + SLOT_GAP)
      scrollContainerRef.current.scrollTop = scrollPosition
    }
  }, [showAll, model.visibleStartIndex])

  if (model.isEmpty) {
    return (
      <div className="flex h-136 flex-col rounded-4xl bg-surface-foreground p-6">
        <div className="flex">
          <Text fontStyle="HS">{word("boxDashboard.dailySchedule.title")}</Text>
        </div>
        <div className="flex flex-1 flex-col items-center justify-center">
          <EmptyView
            title={word("boxDashboard.dailySchedule.empty.title")}
            description={word("boxDashboard.dailySchedule.empty.description")}
            flexGrow={false}
            textAlign="center"
            disposition="column"
            image={{
              src: ProgrammationEmpty,
            }}
            button={
              <Button variant="secondary" size="lg" className="mt-4" onClick={() => boxNavigate(Path.Box.Planning)}>
                {word("boxDashboard.dailySchedule.empty.cta")}
              </Button>
            }
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-141 flex-col rounded-4xl bg-surface-foreground px-6 pt-6 pb-4">
      {/* Header with title and badge */}
      <div className="mb-6 flex shrink-0 items-center gap-2">
        <Text fontStyle="HS">{word("boxDashboard.dailySchedule.title")}</Text>
        <div className="flex h-7 min-w-7 items-center justify-center rounded-full bg-surface-background-float px-2 pt-px">
          <Text fontStyle="BS" className="text-textOnSurface-default">
            {model.totalCount}
          </Text>
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        className="scrollbar-textOnSurface-muted flex flex-1 flex-col overflow-y-auto"
        style={{ gap: SLOT_GAP }}
      >
        {slotsToDisplay.map((slot) => (
          <DailyScheduleCard key={slot.id} slot={slot} onClick={() => void navigateToBoxSlotView(slot.id)} />
        ))}
      </div>

      {/* Show more button */}
      {model.hasMore && !showAll && (
        <div className="mt-6 flex shrink-0 justify-center">
          <Button variant="underline" size="md" onClick={() => setShowAll(true)}>
            {word("boxDashboard.dailySchedule.showMore")}
          </Button>
        </div>
      )}
    </div>
  )
}
